import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const TOOL_URL = 'https://budget.mochee.com';
const BOOKING_URL = 'https://meetings.hubspot.com/franky';

// ── GA4 event helper ──────────────────────────────────────────────────────────
function gtagEvent(eventName, params = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

// ── Data ──────────────────────────────────────────────────────────────────────

const INDUSTRIES = [
  'E-Commerce / Retail', 'SaaS / Software', 'Healthcare', 'Financial Services',
  'Real Estate', 'Education', 'Travel & Hospitality', 'Food & Beverage',
  'Professional Services', 'Manufacturing', 'Non-Profit', 'Other',
];

const COMPANY_SIZES = [
  'Small (1–50 employees)',
  'Mid-Market (51–500 employees)',
  'Enterprise (500+ employees)',
];

const CHANNELS = [
  { key: 'paid_search',        label: 'Paid Search (Google Ads)',  icon: '🔍' },
  { key: 'paid_social',        label: 'Paid Social',               icon: '📱' },
  { key: 'organic_social',     label: 'Organic Social Media',      icon: '📣' },
  { key: 'seo',                label: 'SEO / Organic Search',      icon: '🌱' },
  { key: 'email',              label: 'Email Marketing',           icon: '✉️' },
  { key: 'content_production', label: 'Content Production',        icon: '📸' },
  { key: 'display',            label: 'Display / Programmatic',    icon: '🖥️' },
];

const INDUSTRY_ALLOCATIONS = {
  'E-Commerce / Retail':   { paid_search: 33, paid_social: 25, organic_social: 10, seo: 14, email: 10, content_production: 7,  display: 3  },
  'SaaS / Software':       { paid_search: 28, paid_social: 22, organic_social: 8,  seo: 18, email: 9,  content_production: 8,  display: 7  },
  'Healthcare':            { paid_search: 28, paid_social: 17, organic_social: 10, seo: 22, email: 8,  content_production: 8,  display: 7  },
  'Financial Services':    { paid_search: 32, paid_social: 17, organic_social: 8,  seo: 18, email: 12, content_production: 6,  display: 7  },
  'Real Estate':           { paid_search: 28, paid_social: 28, organic_social: 12, seo: 13, email: 8,  content_production: 7,  display: 4  },
  'Education':             { paid_search: 23, paid_social: 25, organic_social: 12, seo: 18, email: 12, content_production: 8,  display: 2  },
  'Travel & Hospitality':  { paid_search: 27, paid_social: 25, organic_social: 13, seo: 13, email: 12, content_production: 5,  display: 5  },
  'Food & Beverage':       { paid_search: 18, paid_social: 32, organic_social: 16, seo: 12, email: 8,  content_production: 10, display: 4  },
  'Professional Services': { paid_search: 27, paid_social: 17, organic_social: 10, seo: 22, email: 12, content_production: 8,  display: 4  },
  'Manufacturing':         { paid_search: 28, paid_social: 12, organic_social: 7,  seo: 22, email: 17, content_production: 8,  display: 6  },
  'Non-Profit':            { paid_search: 18, paid_social: 25, organic_social: 16, seo: 17, email: 16, content_production: 6,  display: 2  },
  'Other':                 { paid_search: 27, paid_social: 21, organic_social: 10, seo: 17, email: 12, content_production: 7,  display: 6  },
};

// Agency fee tiers for paid_search, paid_social, display (based on channel ad spend)
// organic_social: $5,500 per every 2 channels
// seo: $2,000/month (+ $5,000 one-time setup in month 1)
// email: $2,800/month
// content_production: 4 half-day shoots/year × $3,500 = $14,000 → $1,167/month
function getAgencyFee(key, adSpend, organicSocialCount) {
  if (key === 'paid_search' || key === 'paid_social' || key === 'display') {
    if (adSpend < 15000)  return 2000;
    if (adSpend < 25000)  return Math.round(adSpend * 0.15);
    if (adSpend < 100000) return Math.round(adSpend * 0.125);
    return Math.round(adSpend * 0.10);
  }
  if (key === 'organic_social')     return Math.ceil(organicSocialCount / 2) * 5500;
  if (key === 'seo')                return 2000;
  if (key === 'email')              return 2800;
  if (key === 'content_production') return 1167;
  return 2000;
}

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com',
  'proton.me', 'ymail.com', 'msn.com', 'comcast.net', 'att.net',
  'verizon.net', 'sbcglobal.net', 'bellsouth.net', 'cox.net',
  'zoho.com', 'mail.com', 'gmx.com', 'gmx.net', 'googlemail.com',
]);

function isPersonalEmail(email) {
  const domain = (email.split('@')[1] || '').toLowerCase();
  return PERSONAL_EMAIL_DOMAINS.has(domain);
}

// ── Benchmark data ────────────────────────────────────────────────────────────
const BENCHMARKS = {
  'E-Commerce / Retail': {
    paid_search:        { cpc: 3.49, cvr: 0.0383, cpl: 80,  ctr: 0.0892 },
    paid_social:        { cpc: 0.70, cvr: 0.0326, cpl: 21,  ctr: 0.0159 },
    organic_social:     { cvr: 0.010, engagement_rate: 0.032 },
    display:            { cpc: 0.75, ctr: 0.0065 },
    email:              { open_rate: 0.37, ctr: 0.021 },
    seo:                { cvr: 0.035, roi_x: 4   },
    content_production: {},
  },
  'SaaS / Software': {
    paid_search:        { cpc: 5.58, cvr: 0.0514, cpl: 87,  ctr: 0.0565 },
    paid_social:        { cpc: 1.27, cvr: 0.0231, cpl: 55,  ctr: 0.0078 },
    organic_social:     { cvr: 0.012, engagement_rate: 0.028 },
    display:            { cpc: 1.50, ctr: 0.0040 },
    email:              { open_rate: 0.38, ctr: 0.028 },
    seo:                { cvr: 0.020, roi_x: 7   },
    content_production: {},
  },
  'Healthcare': {
    paid_search:        { cpc: 5.00, cvr: 0.1162, cpl: 35,  ctr: 0.0673 },
    paid_social:        { cpc: 1.32, cvr: 0.1100, cpl: 12,  ctr: 0.0083 },
    organic_social:     { cvr: 0.015, engagement_rate: 0.042 },
    display:            { cpc: 1.40, ctr: 0.0035 },
    email:              { open_rate: 0.44, ctr: 0.032 },
    seo:                { cvr: 0.035, roi_x: 7   },
    content_production: {},
  },
  'Financial Services': {
    paid_search:        { cpc: 3.46, cvr: 0.0255, cpl: 110, ctr: 0.0833 },
    paid_social:        { cpc: 3.77, cvr: 0.0947, cpl: 41,  ctr: 0.0056 },
    organic_social:     { cvr: 0.008, engagement_rate: 0.020 },
    display:            { cpc: 1.80, ctr: 0.0045 },
    email:              { open_rate: 0.39, ctr: 0.030 },
    seo:                { cvr: 0.030, roi_x: 7   },
    content_production: {},
  },
  'Real Estate': {
    paid_search:        { cpc: 2.53, cvr: 0.0328, cpl: 70,  ctr: 0.0843 },
    paid_social:        { cpc: 1.80, cvr: 0.1068, cpl: 16,  ctr: 0.0099 },
    organic_social:     { cvr: 0.018, engagement_rate: 0.038 },
    display:            { cpc: 1.25, ctr: 0.0108 },
    email:              { open_rate: 0.41, ctr: 0.029 },
    seo:                { cvr: 0.025, roi_x: 14  },
    content_production: {},
  },
  'Education': {
    paid_search:        { cpc: 6.23, cvr: 0.1138, cpl: 45,  ctr: 0.0574 },
    paid_social:        { cpc: 1.60, cvr: 0.1358, cpl: 11,  ctr: 0.0073 },
    organic_social:     { cvr: 0.020, engagement_rate: 0.048 },
    display:            { cpc: 0.90, ctr: 0.0055 },
    email:              { open_rate: 0.45, ctr: 0.035 },
    seo:                { cvr: 0.035, roi_x: 9   },
    content_production: {},
  },
  'Travel & Hospitality': {
    paid_search:        { cpc: 2.12, cvr: 0.0575, cpl: 35,  ctr: 0.0873 },
    paid_social:        { cpc: 1.50, cvr: 0.0282, cpl: 45,  ctr: 0.0090 },
    organic_social:     { cvr: 0.014, engagement_rate: 0.055 },
    display:            { cpc: 0.80, ctr: 0.0090 },
    email:              { open_rate: 0.40, ctr: 0.028 },
    seo:                { cvr: 0.020, roi_x: 4.5 },
    content_production: {},
  },
  'Food & Beverage': {
    paid_search:        { cpc: 2.05, cvr: 0.0709, cpl: 28,  ctr: 0.0758 },
    paid_social:        { cpc: 0.74, cvr: 0.0800, cpl: 9,   ctr: 0.0224 },
    organic_social:     { cvr: 0.022, engagement_rate: 0.065 },
    display:            { cpc: 0.75, ctr: 0.0065 },
    email:              { open_rate: 0.43, ctr: 0.026 },
    seo:                { cvr: 0.030, roi_x: 4   },
    content_production: {},
  },
  'Professional Services': {
    paid_search:        { cpc: 5.58, cvr: 0.0514, cpl: 87,  ctr: 0.0565 },
    paid_social:        { cpc: 2.50, cvr: 0.0810, cpl: 25,  ctr: 0.0125 },
    organic_social:     { cvr: 0.012, engagement_rate: 0.025 },
    display:            { cpc: 1.60, ctr: 0.0022 },
    email:              { open_rate: 0.42, ctr: 0.031 },
    seo:                { cvr: 0.050, roi_x: 8   },
    content_production: {},
  },
  'Manufacturing': {
    paid_search:        { cpc: 5.70, cvr: 0.0717, cpl: 65,  ctr: 0.0623 },
    paid_social:        { cpc: 2.14, cvr: 0.0177, cpl: 66,  ctr: 0.0071 },
    organic_social:     { cvr: 0.008, engagement_rate: 0.018 },
    display:            { cpc: 1.30, ctr: 0.0020 },
    email:              { open_rate: 0.3265, ctr: 0.0422 },
    seo:                { cvr: 0.030, roi_x: 5   },
    content_production: {},
  },
  'Non-Profit': {
    paid_search:        { cpc: 1.60, cvr: 0.0484, cpl: 32,  ctr: 0.1310 },
    paid_social:        { cpc: 1.26, cvr: 0.0980, cpl: 12,  ctr: 0.0250 },
    organic_social:     { cvr: 0.025, engagement_rate: 0.070 },
    display:            { cpc: 1.10, ctr: 0.0046 },
    email:              { open_rate: 0.5321, ctr: 0.039 },
    seo:                { cvr: 0.030, roi_x: 7.5 },
    content_production: {},
  },
  'Other': {
    paid_search:        { cpc: 5.26, cvr: 0.0752, cpl: 70,  ctr: 0.0666 },
    paid_social:        { cpc: 1.92, cvr: 0.0772, cpl: 28,  ctr: 0.0157 },
    organic_social:     { cvr: 0.012, engagement_rate: 0.030 },
    display:            { cpc: 1.10, ctr: 0.0046 },
    email:              { open_rate: 0.4346, ctr: 0.0209 },
    seo:                { cvr: 0.024, roi_x: 8.25 },
    content_production: {},
  },
};

// ── Channel narratives (2.2) ──────────────────────────────────────────────────
const CHANNEL_NARRATIVES = {
  paid_search:        'Captures high-intent buyers actively searching for solutions like yours — the highest purchase-intent channel in digital marketing.',
  paid_social:        'Builds brand awareness and drives consideration across Meta, TikTok, LinkedIn, and more. Best for visual storytelling at scale.',
  organic_social:     'Grows community and brand affinity through authentic content. Compounds in value over time and amplifies every paid campaign.',
  seo:                'Attracts qualified organic traffic at the lowest long-term cost per lead. Best ROI of any channel at 4–14× over 12–18 months.',
  email:              'Your highest-converting owned channel. Nurtures existing leads and drives repeat revenue with minimal incremental ad spend.',
  content_production: 'The visual and creative foundation — professional assets that elevate paid, social, and brand across every touchpoint.',
  display:            'Keeps your brand top-of-mind for buyers in-market. Ideal for retargeting, awareness campaigns, and competitive conquesting.',
};

// ── Benchmark source attribution (4.3) ───────────────────────────────────────
const BENCHMARK_SOURCES = {
  paid_search:        'WordStream / LocaliQ',
  paid_social:        'Triple Whale / HockeyStack',
  organic_social:     'Sprout Social',
  display:            'WordStream / LocaliQ',
  email:              'Mailchimp / Brevo',
  seo:                'First Page Sage',
  content_production: 'Mochee Agency Data',
};

// ── Industry average monthly budgets (3.3) ────────────────────────────────────
const INDUSTRY_MONTHLY_AVERAGES = {
  'E-Commerce / Retail':   { small: [5000, 15000],   midmarket: [20000, 65000],  enterprise: [80000, 250000]  },
  'SaaS / Software':       { small: [8000, 25000],   midmarket: [35000, 100000], enterprise: [120000, 400000] },
  'Healthcare':            { small: [5000, 18000],   midmarket: [25000, 70000],  enterprise: [80000, 200000]  },
  'Financial Services':    { small: [8000, 22000],   midmarket: [30000, 90000],  enterprise: [100000, 350000] },
  'Real Estate':           { small: [3000, 12000],   midmarket: [15000, 50000],  enterprise: [60000, 180000]  },
  'Education':             { small: [4000, 15000],   midmarket: [20000, 60000],  enterprise: [70000, 200000]  },
  'Travel & Hospitality':  { small: [5000, 18000],   midmarket: [25000, 80000],  enterprise: [90000, 300000]  },
  'Food & Beverage':       { small: [3000, 10000],   midmarket: [15000, 45000],  enterprise: [60000, 200000]  },
  'Professional Services': { small: [5000, 18000],   midmarket: [22000, 65000],  enterprise: [80000, 250000]  },
  'Manufacturing':         { small: [4000, 14000],   midmarket: [18000, 55000],  enterprise: [70000, 220000]  },
  'Non-Profit':            { small: [2000, 8000],    midmarket: [10000, 35000],  enterprise: [40000, 120000]  },
  'Other':                 { small: [5000, 15000],   midmarket: [20000, 60000],  enterprise: [80000, 250000]  },
};

function getIndustryAverageRange(industry, companySize) {
  const data = INDUSTRY_MONTHLY_AVERAGES[industry] || INDUSTRY_MONTHLY_AVERAGES['Other'];
  if (companySize.includes('Small'))      return data.small;
  if (companySize.includes('Mid-Market')) return data.midmarket;
  return data.enterprise;
}

// ── Seasonality ───────────────────────────────────────────────────────────────
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const SEASONAL    = [1.10, 1.00, 0.95, 0.90, 0.85, 0.80, 0.78, 0.82, 0.90, 1.00, 1.15, 1.20];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

function getChannelMeta(key) {
  return CHANNELS.find((c) => c.key === key) || {
    label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
    icon: '📊',
  };
}

function calcChannels(monthlyAdBudget, industry, selectedChannels, organicSocialCount) {
  const alloc    = INDUSTRY_ALLOCATIONS[industry] || INDUSTRY_ALLOCATIONS['Other'];
  const active   = selectedChannels.length ? selectedChannels : Object.keys(alloc);
  const totalPct = active.reduce((s, k) => s + (alloc[k] || 0), 0);
  const industryBenchmarks = BENCHMARKS[industry] || BENCHMARKS['Other'];

  return active.map((key) => {
    const rawPct    = totalPct > 0 ? ((alloc[key] || 0) / totalPct) * 100 : 0;
    const pct       = Math.round(rawPct);
    const adSpend   = (rawPct / 100) * monthlyAdBudget;
    const agencyFee = getAgencyFee(key, adSpend, organicSocialCount);
    const { label, icon } = getChannelMeta(key);
    const benchmark = industryBenchmarks[key] || {};

    // Compute projected leads (2.1)
    let projLeads   = null;
    let projRevenue = null;
    if (adSpend > 0) {
      if (benchmark.cpl && benchmark.cpl > 0) {
        projLeads = Math.round(adSpend / benchmark.cpl);
      } else if (benchmark.cpc && benchmark.cpc > 0 && benchmark.cvr) {
        projLeads = Math.round((adSpend / benchmark.cpc) * benchmark.cvr);
      }
      if (key === 'seo' && benchmark.roi_x) {
        projRevenue = adSpend * benchmark.roi_x;
      }
    }

    return { key, label, icon, pct, rawPct, adSpend, agencyFee, total: adSpend + agencyFee, benchmark, projLeads, projRevenue };
  });
}

function buildMonthlyForecast(results) {
  const baseAdSpend    = results.reduce((s, c) => s + c.adSpend, 0);
  const agencyFeeTotal = results.reduce((s, c) => s + c.agencyFee, 0);
  const seasonalSum    = SEASONAL.reduce((s, v) => s + v, 0);
  return MONTH_NAMES.map((month, i) => {
    const mult    = SEASONAL[i] * (12 / seasonalSum);
    const adSpend = baseAdSpend * mult;
    return { month, mult, adSpend, agencyFee: agencyFeeTotal, total: adSpend + agencyFeeTotal };
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StepIndicator({ current, total }) {
  return (
    <div className="step-indicator">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`step-dot ${i < current ? 'done' : ''} ${i === current ? 'active' : ''}`} />
      ))}
    </div>
  );
}

function MoneyInput({ value, onChange, placeholder }) {
  const displayValue = value ? Number(value).toLocaleString('en-US') : '';
  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      placeholder={placeholder}
    />
  );
}

function CountStepper({ label, value, onChange, hint }) {
  return (
    <div className="count-stepper-wrap">
      <div className="count-stepper-label">{label}</div>
      <div className="count-stepper">
        <button className="stepper-btn" onClick={() => onChange(Math.max(1, value - 1))}>−</button>
        <span className="stepper-val">{value}</span>
        <button className="stepper-btn" onClick={() => onChange(Math.min(5, value + 1))}>+</button>
      </div>
      {hint && <div className="count-stepper-hint">{hint}</div>}
    </div>
  );
}

// Pipeline headline (2.4)
function PipelineHeadline({ results }) {
  const totalLeads   = results.reduce((s, c) => s + (c.projLeads   || 0), 0);
  const totalRevenue = results.reduce((s, c) => s + (c.projRevenue || 0), 0);
  if (totalLeads === 0 && totalRevenue === 0) return null;

  return (
    <div className="pipeline-headline">
      <div className="ph-eyebrow">Projected Performance</div>
      <div className="ph-stats">
        {totalLeads > 0 && (
          <div className="ph-stat">
            <div className="ph-val">~{totalLeads.toLocaleString()}</div>
            <div className="ph-label">Est. conv. / month</div>
          </div>
        )}
        {totalLeads > 0 && (
          <div className="ph-stat">
            <div className="ph-val">~{(totalLeads * 12).toLocaleString()}</div>
            <div className="ph-label">Est. conv. / year</div>
          </div>
        )}
        {totalRevenue > 0 && (
          <div className="ph-stat ph-stat-green">
            <div className="ph-val">{fmt(totalRevenue)}</div>
            <div className="ph-label">SEO revenue projection / mo</div>
          </div>
        )}
      </div>
      <div className="ph-disclaimer">Estimates based on published industry benchmarks. Actual results vary.</div>
    </div>
  );
}

// Industry average callout (3.3)
function IndustryAverageCallout({ industry, companySize, totalMonthly }) {
  if (!industry || !companySize || !totalMonthly) return null;
  const [low, high] = getIndustryAverageRange(industry, companySize);
  const sizeLabel = companySize.includes('Small') ? 'Small' : companySize.includes('Mid-Market') ? 'Mid-Market' : 'Enterprise';

  let position = 'at the lower end';
  let positionClass = 'pos-low';
  if (totalMonthly >= high) { position = 'above average'; positionClass = 'pos-high'; }
  else if (totalMonthly >= (low + high) / 2) { position = 'right in range'; positionClass = 'pos-mid'; }
  else if (totalMonthly >= low) { position = 'slightly below average'; positionClass = 'pos-low'; }

  return (
    <div className="industry-avg-callout">
      <div className="iac-icon">📊</div>
      <div className="iac-body">
        <div className="iac-title">How your budget compares</div>
        <div className="iac-text">
          {sizeLabel} {industry} companies typically spend <strong>{fmt(low)}–{fmt(high)}/month</strong> on marketing.
          Your plan of <strong>{fmt(totalMonthly)}/mo</strong> is <span className={`iac-position ${positionClass}`}>{position}</span>.
          {totalMonthly < low && ' Prioritizing your top 2–3 channels will maximize returns at this budget level.'}
          {totalMonthly >= high && ' You\'re well-positioned to capture significant market share in your industry.'}
        </div>
      </div>
    </div>
  );
}

// Scenario comparison (3.2)
function ScenarioComparison({ results, totalMonthlyAdSpend, forecast }) {
  const [scenario, setScenario] = useState(null);

  const multipliers = { '20': 1.20, '50': 1.50 };

  if (!scenario) {
    return (
      <div className="scenario-teaser">
        <button className="btn-scenario" onClick={() => setScenario('20')}>
          Compare: What if I increased my budget? →
        </button>
      </div>
    );
  }

  const mult          = multipliers[scenario];
  const newAdBudget   = totalMonthlyAdSpend * mult;
  const delta         = newAdBudget - totalMonthlyAdSpend;
  const newTotalLeads = results.reduce((s, c) => {
    if (!c.projLeads) return s;
    return s + Math.round(c.projLeads * mult);
  }, 0);
  const origLeads = results.reduce((s, c) => s + (c.projLeads || 0), 0);
  const leadDelta = newTotalLeads - origLeads;
  const newMonthlyTotal = results.reduce((s, c) => s + (c.adSpend * mult + c.agencyFee), 0);
  const origMonthlyTotal = results.reduce((s, c) => s + c.total, 0);

  return (
    <div className="scenario-panel">
      <div className="scenario-header">
        <span className="scenario-title">Budget Scenario Comparison</span>
        <button className="scenario-close" onClick={() => setScenario(null)}>✕</button>
      </div>
      <div className="scenario-tabs">
        {['20', '50'].map((s) => (
          <button
            key={s}
            className={`scenario-tab ${scenario === s ? 'active' : ''}`}
            onClick={() => setScenario(s)}
          >
            +{s}% Budget
          </button>
        ))}
      </div>
      <div className="scenario-grid">
        <div className="scenario-col">
          <div className="sc-label">Current</div>
          <div className="sc-val">{fmt(origMonthlyTotal)}<span className="sc-unit">/mo</span></div>
          {origLeads > 0 && <div className="sc-leads">~{origLeads} conv./mo</div>}
        </div>
        <div className="scenario-arrow">→</div>
        <div className="scenario-col scenario-col-new">
          <div className="sc-label">+{scenario}% Budget</div>
          <div className="sc-val">{fmt(newMonthlyTotal)}<span className="sc-unit">/mo</span></div>
          {origLeads > 0 && <div className="sc-leads">~{newTotalLeads} conv./mo</div>}
        </div>
      </div>
      <div className="scenario-deltas">
        <div className="sc-delta">
          <span className="sc-delta-label">Additional ad spend</span>
          <span className="sc-delta-val">+{fmt(delta)}/mo</span>
        </div>
        {leadDelta > 0 && (
          <div className="sc-delta">
            <span className="sc-delta-label">Additional est. leads</span>
            <span className="sc-delta-val sc-delta-green">+{leadDelta} conv./mo</span>
          </div>
        )}
        <div className="sc-delta">
          <span className="sc-delta-label">Annual impact</span>
          <span className="sc-delta-val">+{fmt(delta * 12)}/year in ad spend</span>
        </div>
      </div>
      <div className="scenario-cta">
        <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="btn-ghost btn-sm">
          Discuss this scenario with our team →
        </a>
      </div>
    </div>
  );
}

function ChannelBar({ channelKey, label, icon, pct, rawPct, adSpend, agencyFee, total, benchmark, projLeads, projRevenue, index }) {
  const b = benchmark || {};

  let metricLabel = null;
  let metricValue = null;

  if (channelKey === 'email' && b.open_rate) {
    metricLabel = 'Avg Open Rate';
    metricValue = `${(b.open_rate * 100).toFixed(0)}%`;
  } else if (channelKey === 'seo' && b.cvr) {
    metricLabel = 'Avg CVR';
    metricValue = `${(b.cvr * 100).toFixed(1)}%`;
  } else if (b.cvr) {
    metricLabel = 'Conversion Rate';
    metricValue = `${(b.cvr * 100).toFixed(1)}%`;
  }

  const cplDisplay = b.cpl ? fmt(b.cpl) : null;
  const num = String(index + 1).padStart(2, '0');
  const narrative = CHANNEL_NARRATIVES[channelKey] || null;
  const source    = BENCHMARK_SOURCES[channelKey]  || null;

  return (
    <div className="channel-bar">
      <div className="channel-bar-header">
        <span className="channel-num">{num}</span>
        <span className="channel-icon">{icon}</span>
        <span className="channel-label">{label}</span>
        <span className="channel-pct">{pct}%</span>
      </div>
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${Math.min(rawPct ?? pct, 100)}%` }} />
      </div>
      <div className="channel-costs">
        <span>Agency Fee: <strong>{fmt(agencyFee)}</strong></span>
        <span>Ad Spend: <strong>{fmt(adSpend)}</strong></span>
        {metricLabel && metricValue && <span>{metricLabel}: <strong>{metricValue}</strong></span>}
        {cplDisplay && channelKey !== 'seo' && channelKey !== 'email' && <span>Cost / Lead: <strong>{cplDisplay}</strong></span>}
      </div>
      {/* Narrative (2.2) */}
      {narrative && <div className="channel-narrative">{narrative}</div>}
      {/* Source attribution (4.3) */}
      {source && <div className="channel-source">Source: {source}</div>}
    </div>
  );
}

function ChannelTotals({ results }) {
  const totalAdSpend = results.reduce((s, c) => s + c.adSpend, 0);
  const totalAgency  = results.reduce((s, c) => s + c.agencyFee, 0);
  const totalInvest  = results.reduce((s, c) => s + c.total, 0);
  return (
    <div className="channel-totals">
      <span className="totals-label">Monthly Totals</span>
      <span>Ad Spend: <strong>{fmt(totalAdSpend)}</strong></span>
      <span>Agency Fees: <strong>{fmt(totalAgency)}</strong></span>
      <span className="totals-grand">Total Investment: <strong>{fmt(totalInvest)}</strong></span>
    </div>
  );
}

function SeasonalChart({ forecast }) {
  const maxTotal   = Math.max(...forecast.map((m) => m.total));
  const peakMonths = new Set([0, 10, 11]);
  const dipMonths  = new Set([5, 6, 7]);

  return (
    <div className="seasonal-chart">
      <div className="chart-heading">
        <span className="chart-title">12-Month Budget Forecast</span>
        <span className="chart-sub">Peak Nov–Jan · Low Jun–Aug</span>
      </div>
      <div className="chart-bars">
        {forecast.map(({ month, total }, i) => {
          const heightPct = Math.round((total / maxTotal) * 100);
          return (
            <div key={month} className="chart-col">
              <div className="chart-amount">{fmt(total)}</div>
              <div className="chart-bar-wrap">
                <div
                  className={`chart-bar-fill ${peakMonths.has(i) ? 'peak' : dipMonths.has(i) ? 'dip' : ''}`}
                  style={{ height: `${heightPct}%` }}
                />
              </div>
              <div className="chart-month">{month}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ForecastTable({ results, forecast }) {
  const annualAdSpend = forecast.reduce((s, m) => s + m.adSpend, 0);
  const annualAgency  = forecast.reduce((s, m) => s + m.agencyFee, 0);
  const annualTotal   = forecast.reduce((s, m) => s + m.total, 0);

  return (
    <div className="forecast-table-wrap">
      <div className="forecast-table-heading">Annual Budget Forecast by Channel</div>
      <div className="forecast-scroll">
        <table className="forecast-table">
          <thead>
            <tr>
              <th>Month</th>
              {results.map((r) => <th key={r.key}>{r.icon} {r.label}</th>)}
              <th>Ad Spend</th>
              <th>Agency Fees</th>
              <th className="ft-total">Total</th>
            </tr>
          </thead>
          <tbody>
            {forecast.map(({ month, mult, adSpend, agencyFee, total }) => (
              <tr key={month}>
                <td className="ft-month">{month}</td>
                {results.map((r) => (
                  <td key={r.key}>{fmt(r.adSpend * mult)}</td>
                ))}
                <td>{fmt(adSpend)}</td>
                <td>{fmt(agencyFee)}</td>
                <td className="ft-total">{fmt(total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="ft-month"><strong>Annual</strong></td>
              {results.map((r) => (
                <td key={r.key}><strong>{fmt(r.adSpend * 12)}</strong></td>
              ))}
              <td><strong>{fmt(annualAdSpend)}</strong></td>
              <td><strong>{fmt(annualAgency)}</strong></td>
              <td className="ft-total"><strong>{fmt(annualTotal)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────

export default function App() {
  const [step, setStep]                       = useState(0);
  const [mode, setMode]                       = useState('');
  const [form, setForm]                       = useState({
    name: '', email: '', company: '', industry: '', companySize: '',
    monthlyBudget: '', monthlyRevenueGoal: '', targetRoas: '',
  });
  const [selectedChannels,    setSelectedChannels]    = useState(['paid_search', 'paid_social', 'organic_social', 'content_production']);
  const [paidSocialCount,     setPaidSocialCount]     = useState(2);
  const [organicSocialCount,  setOrganicSocialCount]  = useState(2);
  const [results,             setResults]             = useState(null);
  const [forecast,            setForecast]            = useState(null);
  const [editableResults,     setEditableResults]     = useState([]);
  const [editMode,            setEditMode]            = useState(false);
  const [newChannelName,      setNewChannelName]      = useState('');
  const [newChannelPct,       setNewChannelPct]       = useState('');
  const [totalMonthlyAdSpend, setTotalMonthlyAdSpend] = useState(0);
  const [submitted,           setSubmitted]           = useState(false);
  const [loading,             setLoading]             = useState(false);
  const [error,               setError]               = useState('');
  const [emailError,          setEmailError]          = useState('');
  const [apiOk,               setApiOk]               = useState(null);
  const [utmParams,           setUtmParams]           = useState({});

  // Health check
  useEffect(() => {
    axios.get(`${API_URL}/api/health`)
      .then(() => setApiOk(true))
      .catch(() => setApiOk(false));
  }, []);

  // UTM capture (2.6)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utm = {
      utm_source:   params.get('utm_source')   || '',
      utm_medium:   params.get('utm_medium')   || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term:     params.get('utm_term')     || '',
      utm_content:  params.get('utm_content')  || '',
      utm_adgroup:  params.get('utm_adgroup')  || '',
    };
    if (Object.values(utm).some(Boolean)) {
      setUtmParams(utm);
    }
  }, []);

  const update      = (field) => (e)  => setForm((f) => ({ ...f, [field]: e.target.value }));
  const updateMoney = (field) => (raw) => setForm((f) => ({ ...f, [field]: raw }));

  const toggleChannel = (key) => {
    setSelectedChannels((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleCalculate = () => {
    let monthlyAdBudget;
    if (mode === 'topdown') {
      const totalBudget  = Number(form.monthlyBudget);
      const active       = selectedChannels.length ? selectedChannels : CHANNELS.map((c) => c.key);
      const alloc        = INDUSTRY_ALLOCATIONS[form.industry] || INDUSTRY_ALLOCATIONS['Other'];
      const totalAllocPt = active.reduce((s, k) => s + (alloc[k] || 0), 0);
      // Pass 1: estimate fees using $0 ad spend (returns minimum/flat fee for tiered channels)
      const pass1Fees = active.reduce((s, k) => s + getAgencyFee(k, 0, organicSocialCount), 0);
      const pass1Pool = Math.max(0, totalBudget - pass1Fees);
      // Pass 2: refine with estimated per-channel ad spend
      const pass2Fees = active.reduce((s, k) => {
        const fraction = totalAllocPt > 0 ? (alloc[k] || 0) / totalAllocPt : 0;
        return s + getAgencyFee(k, fraction * pass1Pool, organicSocialCount);
      }, 0);
      monthlyAdBudget = Math.max(0, totalBudget - pass2Fees);
    } else {
      const roas = Number(form.targetRoas) || 4;
      monthlyAdBudget = Number(form.monthlyRevenueGoal) / roas;
    }
    const r = calcChannels(monthlyAdBudget, form.industry, selectedChannels, organicSocialCount);
    const f = buildMonthlyForecast(r);
    setResults(r);
    setForecast(f);
    setEditableResults(r.map((ch) => ({ ...ch })));
    setTotalMonthlyAdSpend(monthlyAdBudget);
    setEditMode(false);
    setStep(4);
    gtagEvent('calculate_clicked', { industry: form.industry, company_size: form.companySize, mode });
  };

  // Edit mode
  const updateEditPct = (key, val) => {
    setEditableResults((prev) => prev.map((ch) => ch.key === key ? { ...ch, pct: Number(val) || 0 } : ch));
  };
  const removeEditChannel = (key) => {
    setEditableResults((prev) => prev.filter((ch) => ch.key !== key));
  };
  const addEditChannel = () => {
    const name = newChannelName.trim();
    const pct  = Number(newChannelPct) || 0;
    if (!name || !pct) return;
    const key = `custom_${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;
    setEditableResults((prev) => [...prev, { key, label: name, icon: '📊', pct, adSpend: 0, agencyFee: 2000, total: 0, projLeads: null, projRevenue: null }]);
    setNewChannelName('');
    setNewChannelPct('');
  };
  const applyEdits = () => {
    const rawTotal   = editableResults.reduce((s, ch) => s + ch.pct, 0);
    const normalized = editableResults.map((ch) => {
      const normalizedPct = rawTotal > 0 ? (ch.pct / rawTotal) * 100 : 0;
      const adSpend = (normalizedPct / 100) * totalMonthlyAdSpend;
      const b = ch.benchmark || {};
      let projLeads = null;
      let projRevenue = null;
      if (adSpend > 0) {
        if (b.cpl && b.cpl > 0) projLeads = Math.round(adSpend / b.cpl);
        else if (b.cpc && b.cpc > 0 && b.cvr) projLeads = Math.round((adSpend / b.cpc) * b.cvr);
        if (ch.key === 'seo' && b.roi_x) projRevenue = adSpend * b.roi_x;
      }
      return { ...ch, pct: Math.round(normalizedPct), adSpend, total: adSpend + ch.agencyFee, projLeads, projRevenue };
    });
    const f = buildMonthlyForecast(normalized);
    setEditableResults(normalized);
    setResults(normalized);
    setForecast(f);
    setEditMode(false);
  };

  const editPctTotal = editableResults.reduce((s, ch) => s + (Number(ch.pct) || 0), 0);

  // Submit
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const totalAdSpend = results.reduce((s, c) => s + c.adSpend, 0);
      await axios.post(`${API_URL}/api/companies`, {
        name:                 form.company,
        email:                form.email,
        contactName:          form.name,
        industry:             form.industry,
        company_size:         form.companySize,
        annual_budget:        Math.round(totalAdSpend * 12),
        cost_per_acquisition: null,
        conversion_rate:      null,
        allocations:          results,
        forecast,
        mode,
        ...utmParams,
      });
      setSubmitted(true);
      gtagEvent('submit_success', { industry: form.industry, company_size: form.companySize });
    } catch (err) {
      const apiMsg = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Error: ${apiMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 5;
  const showPaidSocialCount    = !selectedChannels.length || selectedChannels.includes('paid_social');
  const showOrganicSocialCount = !selectedChannels.length || selectedChannels.includes('organic_social');

  // Share-to-colleague helper (4.4)
  const shareHref = `mailto:?subject=Mochee's Budget Planner — free marketing tool worth 2 minutes&body=${encodeURIComponent(
    `Hi,\n\nI used Mochee's Budget Planner to build our marketing budget plan and thought you'd find it useful.\n\nIt generates a channel-by-channel allocation and a 12-month seasonal forecast tailored to your industry — all in under 2 minutes, completely free.\n\nCheck it out: ${TOOL_URL}\n\nBest`
  )}`;

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-inner">
          <img className="logo-img" src="https://mochee.com/mocheelogo-white.png" alt="Mochee" />
          <div className="header-tagline">Mochee's Budget Planner</div>
          {apiOk === false && (
            <div className="api-warning">⚠ API offline — results won't be saved</div>
          )}
        </div>
      </header>

      <main className="main">

        {/* Step 0 — Hero */}
        {step === 0 && (
          <section className="hero">
            <div className="hero-content">
              <div className="hero-eyebrow">Budget Planner</div>
              <h1 className="hero-title">
                Allocate your <em>marketing</em><br />
                budget.
              </h1>
              <p className="hero-sub">
                Get a data-backed channel allocation and 12-month budget forecast
                tailored to your industry and business goals from a company 15 years in the business — in under 2 minutes.
              </p>
              <button className="btn-primary btn-large" onClick={() => { setStep(1); gtagEvent('start_clicked'); }}>
                Get My Budget Plan →
              </button>

            </div>
            <div className="hero-visual">
              <div className="visual-card vc1">
                <span className="vc-num">01</span>
                <div className="vc-label">Paid Search</div>
                <div className="vc-bar" style={{ width: '72%' }} />
                <div className="vc-val">33%</div>
              </div>
              <div className="visual-card vc2">
                <span className="vc-num">02</span>
                <div className="vc-label">Paid Social</div>
                <div className="vc-bar" style={{ width: '60%' }} />
                <div className="vc-val">25%</div>
              </div>
              <div className="visual-card vc3">
                <span className="vc-num">03</span>
                <div className="vc-label">Organic Social</div>
                <div className="vc-bar" style={{ width: '35%' }} />
                <div className="vc-val">10%</div>
              </div>
              <div className="visual-card vc4">
                <span className="vc-num">04</span>
                <div className="vc-label">SEO</div>
                <div className="vc-bar" style={{ width: '40%' }} />
                <div className="vc-val">17%</div>
              </div>
            </div>
          </section>
        )}

        {/* Step 1 — Contact info */}
        {step === 1 && (
          <div className="card-form">
            <StepIndicator current={0} total={totalSteps} />
            <h2>Let's start with you</h2>
            <p className="card-sub">We'll send your personalized budget plan to your inbox.</p>
            <div className="field-group">
              <label>Your Name</label>
              <input placeholder="Jane Smith" value={form.name} onChange={update('name')} />
            </div>
            <div className="field-group">
              <label>Work Email</label>
              <input
                type="email"
                placeholder="jane@company.com"
                value={form.email}
                onChange={(e) => { update('email')(e); setEmailError(''); }}
              />
              {emailError && <div className="field-error">{emailError}</div>}
            </div>
            <div className="field-group">
              <label>Company Name</label>
              <input placeholder="Acme Corp" value={form.company} onChange={update('company')} />
            </div>
            <button
              className="btn-primary"
              disabled={!form.name || !form.email || !form.company}
              onClick={() => {
                if (isPersonalEmail(form.email)) {
                  setEmailError('Please use your work email address.');
                  return;
                }
                setStep(2);
                gtagEvent('step_advance', { step: 1 });
              }}
            >
              Continue →
            </button>
          </div>
        )}

        {/* Step 2 — Company details */}
        {step === 2 && (
          <div className="card-form">
            <StepIndicator current={1} total={totalSteps} />
            <h2>About your business</h2>
            <p className="card-sub">We use this to apply the right industry benchmarks to your plan.</p>
            <div className="field-group">
              <label>Industry</label>
              <select value={form.industry} onChange={update('industry')}>
                <option value="">Select industry…</option>
                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className="field-group">
              <label>Company Size</label>
              <select value={form.companySize} onChange={update('companySize')}>
                <option value="">Select size…</option>
                {COMPANY_SIZES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="btn-row">
              <button className="btn-ghost" onClick={() => setStep(1)}>← Back</button>
              <button
                className="btn-primary"
                disabled={!form.industry || !form.companySize}
                onClick={() => { setStep(3); gtagEvent('step_advance', { step: 2 }); }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Budget mode + channels */}
        {step === 3 && (
          <div className="card-form">
            <StepIndicator current={2} total={totalSteps} />
            <h2>How would you like to calculate?</h2>
            <p className="card-sub">Choose the approach that best fits your business.</p>

            <div className="mode-cards">
              <div className={`mode-card ${mode === 'topdown' ? 'selected' : ''}`} onClick={() => setMode('topdown')}>
                <div className="mode-icon">💰</div>
                <div className="mode-title">I have a budget</div>
                <div className="mode-desc">Tell us your monthly marketing budget and we'll show how to allocate it across channels.</div>
              </div>
              <div className={`mode-card ${mode === 'bottomup' ? 'selected' : ''}`} onClick={() => setMode('bottomup')}>
                <div className="mode-icon">🎯</div>
                <div className="mode-title">I have goals</div>
                <div className="mode-desc">Tell us your monthly revenue target and we'll work backwards to the budget you need.</div>
              </div>
            </div>

            {mode === 'topdown' && (
              <div className="field-group" style={{ marginTop: '1.5rem' }}>
                <label>Total Monthly Marketing Budget <span className="label-hint">(including agency fees)</span></label>
                <div className="input-prefix">
                  <span>$</span>
                  <MoneyInput value={form.monthlyBudget} onChange={updateMoney('monthlyBudget')} placeholder="20,000" />
                </div>
              </div>
            )}

            {mode === 'bottomup' && (
              <>
                <div className="field-group" style={{ marginTop: '1.5rem' }}>
                  <label>Monthly Revenue Goal</label>
                  <div className="input-prefix">
                    <span>$</span>
                    <MoneyInput value={form.monthlyRevenueGoal} onChange={updateMoney('monthlyRevenueGoal')} placeholder="100,000" />
                  </div>
                </div>
                <div className="field-group">
                  <label>Target ROAS <span className="label-hint">(optional — defaults to 4×)</span></label>
                  <input type="number" placeholder="4" value={form.targetRoas} onChange={update('targetRoas')} />
                  <p className="card-sub" style={{ marginTop: '0.35rem', marginBottom: 0, fontSize: '0.78rem' }}>
                    e.g. 4 means $4 revenue for every $1 spent on ads
                  </p>
                </div>
              </>
            )}

            {mode && (
              <>
                <div className="field-group" style={{ marginTop: '1.5rem' }}>
                  <label>Channels to include <span className="label-hint">(select all you plan to use throughout the year)</span></label>
                  <div className="channel-toggles">
                    {CHANNELS.map((ch) => (
                      <button
                        key={ch.key}
                        className={`channel-toggle ${selectedChannels.includes(ch.key) ? 'on' : ''}`}
                        onClick={() => toggleChannel(ch.key)}
                      >
                        {ch.icon} {ch.label}
                      </button>
                    ))}
                  </div>
                </div>

                {(showPaidSocialCount || showOrganicSocialCount) && (
                  <div className="social-counts">
                    {showPaidSocialCount && (
                      <CountStepper label="📱 Paid Social Platforms" value={paidSocialCount} onChange={setPaidSocialCount} hint="e.g. Meta Ads, TikTok Ads, Reddit Ads" />
                    )}
                    {showOrganicSocialCount && (
                      <CountStepper label="📣 Organic Social Channels" value={organicSocialCount} onChange={setOrganicSocialCount} hint="e.g. Instagram, TikTok, Facebook, LinkedIn" />
                    )}
                  </div>
                )}
              </>
            )}

            {mode && (() => {
              const activeChs = selectedChannels.length ? selectedChannels : CHANNELS.map((c) => c.key);
              const totalFees = activeChs.reduce((s, k) => s + getAgencyFee(k, 0, organicSocialCount), 0);
              const budget = mode === 'topdown'
                ? Number(form.monthlyBudget)
                : Number(form.monthlyRevenueGoal) / (Number(form.targetRoas) || 4);
              const tooManyChannels = activeChs.length > 5;
              const feesOverBudget  = budget > 0 && totalFees / budget > 0.65;
              if (!tooManyChannels && !feesOverBudget) return null;
              return (
                <div className="channel-warning">
                  ⚠️ {tooManyChannels
                    ? `You've selected ${activeChs.length} channels. We recommend starting on 3–5 high-impact channels for better results at most budget levels.`
                    : `Agency management fees (${fmt(totalFees)}/mo) represent a large share of your budget. Consider reducing the number of channels or increasing your total budget.`
                  }
                </div>
              );
            })()}

            <div className="btn-row">
              <button className="btn-ghost" onClick={() => setStep(2)}>← Back</button>
              <button
                className="btn-primary"
                disabled={!mode || (mode === 'topdown' && !form.monthlyBudget) || (mode === 'bottomup' && !form.monthlyRevenueGoal)}
                onClick={handleCalculate}
              >
                Build My Plan →
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — Results */}
        {step === 4 && results && forecast && !submitted && (
          <div className="results-wrapper">
            <div className="results-header">
              <StepIndicator current={3} total={totalSteps} />
              <h2>Your Budget Planner</h2>
              <p className="card-sub">
                Based on {form.industry} industry benchmarks for a {form.companySize} company. All figures shown per month.
              </p>
            </div>

            {/* 3 KPI summary cards */}
            <div className="results-summary">
              <div className="summary-stat">
                <div className="stat-label">Ad Spend / Month</div>
                <div className="stat-value">{fmt(results.reduce((s, c) => s + c.adSpend, 0))}</div>
              </div>
              <div className="summary-stat">
                <div className="stat-label">Agency Fees / Month</div>
                <div className="stat-value">{fmt(results.reduce((s, c) => s + c.agencyFee, 0))}</div>
              </div>
              <div className="summary-stat highlight">
                <div className="stat-label">Total Investment / Month</div>
                <div className="stat-value">{fmt(results.reduce((s, c) => s + c.total, 0))}</div>
              </div>
            </div>

            {/* Industry average callout (3.3) */}
            <IndustryAverageCallout
              industry={form.industry}
              companySize={form.companySize}
              totalMonthly={results.reduce((s, c) => s + c.total, 0)}
            />

            {/* 12-month chart */}
            <SeasonalChart forecast={forecast} />

            {/* Channel list — view or edit mode */}
            {!editMode ? (
              <>
                <div className="channels-list">
                  {results.map((r, i) => (
                    <ChannelBar key={r.key} channelKey={r.key} index={i} {...r} />
                  ))}
                </div>
                <ChannelTotals results={results} />
                <div style={{ marginBottom: '1.5rem', textAlign: 'right' }}>
                  <button className="btn-ghost" onClick={() => setEditMode(true)}>✏️ Edit Channels</button>
                </div>
              </>
            ) : (
              <div className="channel-edit-panel">
                <div className="channel-edit-header">
                  <span>Edit Channel Allocations</span>
                  <span className={`pct-counter ${editPctTotal !== 100 ? 'pct-warn' : ''}`}>
                    Total: {editPctTotal}% {editPctTotal !== 100 && '(will be normalized to 100%)'}
                  </span>
                </div>
                {editableResults.map((ch) => (
                  <div key={ch.key} className="edit-channel-row">
                    <span className="edit-ch-icon">{ch.icon}</span>
                    <span className="edit-ch-label">{ch.label}</span>
                    <div className="edit-pct-wrap">
                      <input type="number" className="edit-pct-input" value={ch.pct} min="0" max="100"
                        onChange={(e) => updateEditPct(ch.key, e.target.value)} />
                      <span>%</span>
                    </div>
                    <button className="btn-remove" onClick={() => removeEditChannel(ch.key)}>✕</button>
                  </div>
                ))}
                <div className="add-channel-row">
                  <input type="text" placeholder="Channel name" value={newChannelName}
                    onChange={(e) => setNewChannelName(e.target.value)} className="add-ch-name" />
                  <div className="edit-pct-wrap">
                    <input type="number" placeholder="0" value={newChannelPct} min="0" max="100"
                      onChange={(e) => setNewChannelPct(e.target.value)} className="edit-pct-input" />
                    <span>%</span>
                  </div>
                  <button className="btn-ghost" onClick={addEditChannel} disabled={!newChannelName.trim() || !newChannelPct}>+ Add</button>
                </div>
                <div className="btn-row" style={{ marginTop: '1rem' }}>
                  <button className="btn-ghost" onClick={() => { setEditableResults(results.map((r) => ({ ...r }))); setEditMode(false); }}>Cancel</button>
                  <button className="btn-primary" onClick={applyEdits} disabled={editableResults.length === 0}>Apply Changes</button>
                </div>
              </div>
            )}

            {/* 12-month forecast table */}
            <ForecastTable results={results} forecast={forecast} />

            {/* Scenario comparison (3.2) */}
            <ScenarioComparison results={results} totalMonthlyAdSpend={totalMonthlyAdSpend} forecast={forecast} />

            {/* CTA — reframed copy (3.6) */}
            <div className="results-cta">
              <div className="cta-eyebrow">Your plan is ready.</div>
              <h3>Save it. Share it. <em>Act on it.</em></h3>
              <p>
                Enter your email below and we'll send your full Budget Planner report — branded PDF
                and 12-month forecast — straight to your inbox. Our team will follow up with a personalized
                strategy within 1 business day.
              </p>
              {error && <div className="error-msg">{error}</div>}
              <div className="cta-actions">
                <button className="btn-cta-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Sending…' : 'Send Me My Plan →'}
                </button>
                <button className="btn-cta-ghost" onClick={() => setStep(3)}>← Recalculate</button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5 — Thank you */}
        {submitted && (
          <div className="thankyou">
            <div className="ty-icon">✦</div>
            <h2>You're all set, {form.name.split(' ')[0]}!</h2>
            <p>
              Your Budget Planner report is on its way to <strong>{form.email}</strong> — including a
              branded <strong>PDF</strong> and <strong>CSV</strong> with your full channel
              allocation and 12-month forecast. Our team will follow up within 1 business day.
            </p>

            <div className="ty-allocation">
              {results.map((r) => (
                <div key={r.key} className="ty-row">
                  <span>{r.icon} {r.label}</span>
                  <span>{fmt(r.total)}/mo</span>
                </div>
              ))}
              <div className="ty-row ty-total-row">
                <span>Total Monthly Investment</span>
                <span>{fmt(results.reduce((s, c) => s + c.total, 0))}/mo</span>
              </div>
            </div>

            {/* Calendar booking CTA (3.1) */}
            <div className="ty-booking">
              <div className="ty-booking-title">Want to move faster?</div>
              <p className="ty-booking-desc">Book a free 30-minute strategy session. We'll review your allocation and build a custom playbook — no pitch, no pressure.</p>
              <a className="btn-primary" href={BOOKING_URL} target="_blank" rel="noreferrer">
                Book My Free Strategy Session →
              </a>
            </div>

            {/* Send to colleague (4.4) */}
            <div className="ty-share">
              <div className="ty-share-label">Know a CMO or CFO who'd find this useful?</div>
              <a className="btn-share" href={shareHref}>
                Share this tool →
              </a>
            </div>
          </div>
        )}
      </main>

      <footer className="footer">
        <img className="footer-logo" src="https://mochee.com/mocheelogo-white.png" alt="Mochee" />
        <div>© {new Date().getFullYear()} Mochee Digital Marketing. All rights reserved.</div>
        <div>Results based on industry benchmarks. Actual results may vary.</div>
      </footer>
    </div>
  );
}
